import React, {ReactNode} from "react";
import {Checkbox} from "shared/ui";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableResult, TableRow} from "shared/ui/table";
import {useTranslation} from "react-i18next";
import {EmptySvg} from "../../../../shared/assets/svgs/EmptySvg";
import {LoadingSpinner} from "../../../../shared/assets/svgs/LoadingSpinner";

type Column<T> = {
  key: string;
  title: React.ReactNode;
  className?: string;
  render: (row: T) => React.ReactNode;
};

type Props<T> = {
  rows: T[];
  columns: Column<T>[];
  getRowKey: (row: T) => string;
  isLoading: boolean;
  error?: unknown;
  errorLabel?: ReactNode;
  emptyLabel?: ReactNode;
  loadingLabel?: ReactNode;
  gridTemplateColumns: string;
  checkboxColumnWidth?: string;
  selectedKeySet: Set<string>;
  isAllSelected: boolean;
  isPartiallySelected: boolean;
  onToggleAll: (checked: boolean) => void;
  onToggleRow: (row: T, checked: boolean) => void;
};

export const SelectableTable = <T, >({
                                       rows,
                                       columns,
                                       getRowKey,
                                       isLoading,
                                       error,
                                       errorLabel,
                                       emptyLabel,
                                       loadingLabel,
                                       gridTemplateColumns,
                                       selectedKeySet,
                                       isAllSelected,
                                       isPartiallySelected,
                                       onToggleAll,
                                       onToggleRow,
                                     }: Props<T>) => {
  const {t} = useTranslation(["common"]);
  const renderBody = () => {
    if (isLoading) {
      return (<TableResult>{loadingLabel ?? <LoadingSpinner size={24} />}</TableResult>);
    }
    
    if (error) {
      return (<TableResult type={"error"}>{errorLabel}</TableResult>);
    }
    
    if (rows.length === 0) {
      return (<TableResult>{emptyLabel ?? <div className={"flex flex-col items-center justify-center gap-5"}>
          <EmptySvg className={"w-20"} />
        {t("No Data")}
      </div>}</TableResult>);
    }
    
    return rows.map((row) => {
      const rowKey = getRowKey(row);
      const isSelected = selectedKeySet.has(rowKey);
      
      return (<TableRow
        key={rowKey}
        data-state={isSelected ? "selected" : undefined}
        onClick={() => onToggleRow(row, !isSelected)}
        style={{gridTemplateColumns}}
      >
        <TableCell onClick={(event) => event.stopPropagation()}>
          <Checkbox
            checked={isSelected}
            onChange={(event) => onToggleRow(row, event.target.checked)}
          />
        </TableCell>
        {columns.map((column) => (<TableCell key={column.key} className={column.className}>
          {column.render(row)}
        </TableCell>))}
      </TableRow>);
    });
  };
  
  return (<Table>
    <TableHeader>
      <TableRow style={{gridTemplateColumns}}>
        <TableHead>
          <Checkbox
            checked={isAllSelected}
            indeterminate={isPartiallySelected}
            onChange={(event) => onToggleAll(event.target.checked)}
          />
        </TableHead>
        {columns.map((column) => (<TableHead key={column.key}>{column.title}</TableHead>))}
      </TableRow>
    </TableHeader>
    <TableBody>
      {renderBody()}
    </TableBody>
  </Table>);
};
